import {Utils} from 'neo-common';
import {GameModuleResponse} from './GameModuleResponse';
import {IReel} from '../Types/IReel';
import {IWin} from '../Types/IWin';
import {BetType} from '../Types/BetType';

/**
 * @class GameModuleBaseResponse - game module response type of module "base", eq: ResponseBase->modules["base"].
 */
export class GameModuleBaseResponse extends GameModuleResponse {
    /**
     * @property spinId - Current spin, usually 0, increments for each respin.
     */
    spinId: number;

    /**
     * @property respin - Availability of respin, where each bit represents reel.
     */
    respin: number;

    /**
     * @property gameMultiplier - Game wide multiplier.
     * Optional.
     */
    gameMultiplier: number;

    /**
     * @property screen - Contains information about reels and symbols on the screen
     */
    screen: {
        /**
         * @property reels - Contains informations each reel on screen
         */
        reels: Array<IReel>;

        /**
         * @property customData - Custom screen data specific to game module. Optional.
         */
        customData?: any;
    };

    /**
     * @property wins - List of wins as a result of the slot spin.
     */
    wins: Array<IWin>;

    /**
     * @property currentWin - Current win for spin in cents (sum of cash wins from /slots/wins).
     */
    currentWin: number;

    /**
     * @property totalWin - Sum of all spin wins in game round in cents (including current spin).
     */
    totalWin: number;

    /**
     * @property initData - Slot initialization data. Sent only for Initialize request.
     */
    initData: {
        /**
         * @property reels - Slot reel symbols. Each reel is represented as int[].
         * Each item represents symbol on reel (symbol ID).
         */
        reels: Array<Array<number>>;

        /**
         * @property betSettings - Bet configuration.
         */
        betSettings: {
            /**
             * @property betType - Type of allowed bet for slot.
             */
            betType: BetType;

            /**
             * @property coins - Dictionary of available coin sizes per coin count.
             */
            coins?: {[coinSize: number]: Array<number>};

            /**
             * @property coinSizes - List of available coin sizes. Each value represents amount of cents.
             * Sent only when betType is Payline.
             */
            coinSizes?: Array<number>;

            /**
             * @property coinCounts - List of available coin counts.
             * Sent only when betType is Payline.
             */
            coinCounts?: Array<number>;

            /**
             * @property paylineCounts - List of available paylines count.
             * Sent only when betType is Payline.
             */
            paylineCounts?: Array<number>;

            /**
             * @property totalBets - List of available total bets. Each value represents amount of cents.
             * Sent only when betType is Total.
             */
            totalBets?: Array<number>;
        };

        /**
         * @property lastBet - Last bet used.
         */
        lastBet: {
            /**
             * @property betType - Type of bet for slot.
             */
            betType: BetType;

            /**
             * @property coinSize - Value represents amount of cents.
             * Sent only when betType is Payline.
             */
            coinSize?: number;

            /**
             * @property coinCount - List of available coin counts.
             * Sent only when betType is Payline.
             */
            coinCount?: number;

            /**
             * @property paylineCount - Count of paylines included in bet.
             * Sent only when betType is Payline.
             */
            paylineCount?: number;

            /**
             * @property totalBet - Value represents amount of cents.
             * Sent only when betType is Total.
             */
            totalBet?: number;
        },

        /**
         * @property customData - Custom slot initialization data specific to game module.
         * Optional.
         */
        customData?: any;
    };

    protected internalValidate(errors: Array<string>): void {
        if (Utils.isNullOrUndefined(this.spinId)) {
            errors.push('Property "spinId" is missing or has null/undefined value!');
        }
        // TODO(psulek) add check of other required properties...
    }

    public moduleName(): string {
        return 'base';
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Communication/Game/Responses/GameModuleBaseResponse.ts
 **/